import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "@/i18n/LocaleLink";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useSeo } from "@/hooks/useSeo";

const COPY = {
  en: {
    title: "Payment not completed | Women's Den",
    heading: "No payment was completed.",
    body: "Nothing was reserved and you haven't been charged. You can simply try again whenever you're ready.",
    back: "Back to the workshop",
  },
  nl: {
    title: "Betaling niet voltooid | Women's Den",
    heading: "De betaling is niet voltooid.",
    body: "Er is niets gereserveerd en er is niets afgeschreven. Je kunt het gewoon opnieuw proberen wanneer je klaar bent.",
    back: "Terug naar de workshop",
  },
};

const OFFER_ROUTES = {
  "active-birth": "/offerings/active-birth",
  "birth-art": "/offerings/birth-art",
  "mums-babies": "/offerings/mums-babies-circle",
  "womens-circle": "/womens-circle",
};

export default function PaymentCancel() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const [params] = useSearchParams();
  const backTo = OFFER_ROUTES[params.get("offer")] || "/contact";
  useSeo({ title: c.title, description: c.heading, noindex: true });

  const sessionId = params.get("session_id");
  useEffect(() => {
    if (!sessionId) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings/release`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId }),
    }).catch(() => {});
  }, [sessionId]);

  return (
    <main className="bg-cream min-h-screen flex items-center" data-testid="payment-cancel-page">
      <div className="mx-auto max-w-2xl px-6 py-32 text-center w-full">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-ink" data-testid="payment-cancel-heading">{c.heading}</h1>
          <p className="mt-8 text-base leading-relaxed text-soft">{c.body}</p>
          <Link to={backTo} data-testid="payment-cancel-back"
            className="group mt-10 inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust">
            <ArrowLeft size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:-translate-x-0.5" />
            {c.back}
          </Link>
        </motion.div>
      </div>
    </main>
  );
}
